/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from "react";
import { ApiError } from "@/types/apiResponse";

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
}


// Overload 1: API function takes no params
export function useApi<T>(apiFunction: () => Promise<T>): {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  execute: () => Promise<T>;
};

// Overload 2: API function takes params
export function useApi<T, P>(
  apiFunction: (params: P) => Promise<T>
): {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  execute: (params: P) => Promise<T>;
};

// Unified implementation
export function useApi<T, P>(
  apiFunction: ((params: P) => Promise<T>) | (() => Promise<T>)
) {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(
    async (params?: P) => {
      setState({ data: null, loading: true, error: null });
      try {
        const data = await (apiFunction as any)(params);
        setState({ data, loading: false, error: null });
        return data;
      } catch (error) {
        const apiError = error as ApiError;
        setState({ data: null, loading: false, error: apiError });
        return apiError;
      }
    },
    [apiFunction]
  );

  return { ...state, execute: execute as any };
}
